import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import CampaignDispatcher from "dispatchers/campaign-dispatcher";
import Field from "util/components/field";
import Validator from "util/form-validator";
import ModalButtonGroup from "visual-components/util/buttons/ModalButtonGroup";
import { H3, H4 } from "visual-components/util/texts";
import { FullPageSnippetPanel } from "./FullPageSnippetPanel";
import { IframeSnippetPanel } from "./IframeSnippetPanel";

const validations = {
  baseUrl: [Validator.required, Validator.IsWebsite],
};

/**
 * Modal content for the "Self Host" campaign hosting option. Orchestrates tab
 * switching between the legacy full-page snippet and the iframe-only snippet,
 * owns the shared baseUrl field, and hands activation back to the parent modal.
 *
 * @param {Object} props
 * @param {Object} props.currentCampaign - Redux currentCampaign slice (campaign being edited).
 * @param {Object} props.currentCampaignForm - Redux form state for currentCampaign.
 * @param {Object} props.currentBrand - The brand creating the campaign.
 * @param {Function} props.activateHosting - Callback invoked with the chosen hosting type after validation.
 * @param {Function} props.deselectOption - Callback to close the modal without activating.
 * @param {Function} props.updateAttr - Generic attribute updater for the baseUrl Field.
 * @returns {import("react").ReactElement}
 */
export function SelfHost(props) {
  const { currentCampaign, currentCampaignForm, currentBrand, updateAttr } = props;

  const [validURL, setValidURL] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setValidURL(currentCampaign.baseUrl);
  }, [currentCampaign.baseUrl]);

  /**
   * Validate baseUrl and activate the "selfHost" hosting type.
   * @returns {void}
   */
  const handleActivate = () => {
    Validator.validateForm(validations, currentCampaign, "currentCampaignForm", (valid) => {
      if (valid) props.activateHosting("selfHost");
    });
  };

  /**
   * Restore the previously valid baseUrl and close the modal.
   * @returns {void}
   */
  const handleCancel = () => {
    CampaignDispatcher.updateAttr("baseUrl", validURL);
    props.deselectOption();
  };

  return (
    <div>
      <div className="row text-center">
        <div className="col-sm-12">
          <H3>Install our Code in your Website</H3>
        </div>
      </div>
      <br />
      <div className="row text-left">
        <div className="col-sm-12">
          <H4 multiline>This option requires a developer.</H4>
        </div>
      </div>
      <br />

      <div className="row text-left">
        <div className="col-sm-12">
          <Tabs
            value={activeTab}
            onChange={(_event, value) => setActiveTab(value)}
            aria-label="Snippet variant tabs"
          >
            <Tab label="Full Page" />
            <Tab label="Iframe Only" />
          </Tabs>
        </div>
      </div>

      {activeTab === 0 && (
        <FullPageSnippetPanel currentCampaign={currentCampaign} currentBrand={currentBrand} />
      )}
      {activeTab === 1 && <IframeSnippetPanel campaignUid={currentCampaign.uid} />}

      <br />

      <div className="row text-left">
        <div className="col-sm-12">
          <Field
            editable
            dataModel={currentCampaign}
            formModel={currentCampaignForm}
            attrName="baseUrl"
            validations={validations.baseUrl}
            updateAttr={updateAttr}
            label="Enter Giveaway URL"
            placeholder="http://www.yourcompany/giveaway-url"
          />
        </div>
      </div>

      <div className="row text-center">
        <div className="col-sm-12">
          <ModalButtonGroup
            hideLine={false}
            canSave
            confirmText="Activate"
            cancelText="Cancel"
            confirm={handleActivate}
            cancel={handleCancel}
          />
        </div>
      </div>
    </div>
  );
}
